import React, { useEffect, useState } from "react";
import { orendaOidcConfig } from "../../config/orendaOidcConfig";

// Landing page for Cognito's redirect after successful sign-in.
// Instead of silently exchanging the code and closing the popup, this page
// dumps EVERYTHING to the screen so we can inspect exactly what Cognito
// returned end-to-end.

const HOSTED_UI = "https://bopayments.auth.eu-west-1.amazoncognito.com";
const TOKEN_URL = `${HOSTED_UI}/oauth2/token`;

const decodeJwt = (token) => {
  if (!token) return null;
  try {
    const payload = token.split(".")[1];
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(normalized));
  } catch (e) {
    return { _decodeError: e.message };
  }
};

const copy = (text) => {
  if (!text) return;
  navigator.clipboard.writeText(text);
};

const parseQuery = (search) => {
  const params = new URLSearchParams(search);
  const out = {};
  params.forEach((v, k) => {
    out[k] = v;
  });
  return out;
};

const OrendaCallback = () => {
  const [step, setStep] = useState("parsing");
  const [error, setError] = useState(null);
  const [landing, setLanding] = useState({});
  const [tokenRequest, setTokenRequest] = useState(null);
  const [tokenResponse, setTokenResponse] = useState(null);
  const [rawResponseText, setRawResponseText] = useState(null);
  const [httpStatus, setHttpStatus] = useState(null);

  useEffect(() => {
    const run = async () => {
      // 1. Capture what landed on this page from Cognito
      const url = window.location.href;
      const query = parseQuery(window.location.search);
      const stored = window.sessionStorage.getItem(
        `oidc.${query.state || ""}`
      );

      setLanding({ url, query, storedState: stored });

      // If Cognito redirected with an error, stop here.
      if (query.error) {
        setError(`Cognito returned error: ${query.error} — ${query.error_description || "no description"}`);
        setStep("error");
        return;
      }

      if (!query.code) {
        setError("No `code` parameter in the URL — nothing to exchange.");
        setStep("error");
        return;
      }

      // 2. Try to recover the PKCE code_verifier that was stored on sign-in.
      // oidc-client-ts stores it under a key like `oidc.<state>` in sessionStorage.
      let code_verifier = null;
      try {
        const entry = JSON.parse(stored || "{}");
        code_verifier = entry.code_verifier;
      } catch {
        // ignore — will surface below
      }

      if (!code_verifier) {
        setError(
          "Could not find the PKCE code_verifier in sessionStorage. " +
          "This usually means: (a) you're testing the callback URL without having started sign-in from this same browser, or " +
          "(b) the popup opened on a different origin than the parent (localhost vs card-console.com) so sessionStorage is not shared."
        );
        setStep("error");
        return;
      }

      // 3. Build and show the exact token-exchange request we're about to send.
      const body = new URLSearchParams({
        grant_type: "authorization_code",
        client_id: orendaOidcConfig.client_id,
        code: query.code,
        redirect_uri: orendaOidcConfig.redirect_uri,
        code_verifier,
      });

      setTokenRequest({
        method: "POST",
        url: TOKEN_URL,
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setStep("exchanging");

      // 4. Fire the request and capture the raw response.
      try {
        const res = await fetch(TOKEN_URL, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: body.toString(),
        });
        setHttpStatus(res.status);
        const text = await res.text();
        setRawResponseText(text);
        try {
          const json = JSON.parse(text);
          setTokenResponse(json);
        } catch {
          // non-JSON response — leave raw text visible
        }
        setStep(res.ok ? "done" : "http-error");
      } catch (e) {
        setError(`Network error during token exchange: ${e.message}`);
        setStep("error");
      }
    };
    run();
  }, []);

  const idClaims = tokenResponse?.id_token ? decodeJwt(tokenResponse.id_token) : null;
  const accessClaims = tokenResponse?.access_token
    ? decodeJwt(tokenResponse.access_token)
    : null;

  return (
    <div style={pageStyle}>
      <h2 style={{ marginTop: 0 }}>Orenda Callback — Live Response</h2>
      <p style={{ color: "#6b6f78" }}>
        Cognito redirected here after sign-in. Everything the flow produced is
        printed below for inspection.
      </p>

      <Section label="Step" value={step} />

      {error && (
        <div style={{ ...cardStyle, borderColor: "#a3272a" }}>
          <strong style={{ color: "#a3272a" }}>Error</strong>
          <div style={{ marginTop: 6 }}>{error}</div>
        </div>
      )}

      <Card title="1 · What Cognito sent to this URL">
        <KV k="Full URL" v={landing.url} copyable />
        <KV k="Query params" v={JSON.stringify(landing.query, null, 2)} pre />
        <KV
          k="sessionStorage entry (oidc.<state>)"
          v={landing.storedState || "(not present)"}
          pre
        />
      </Card>

      {tokenRequest && (
        <Card title="2 · Token exchange request we sent">
          <KV k="Method" v={tokenRequest.method} />
          <KV k="URL" v={tokenRequest.url} copyable />
          <KV k="Headers" v={JSON.stringify(tokenRequest.headers, null, 2)} pre />
          <KV k="Body" v={tokenRequest.body} pre copyable />
        </Card>
      )}

      {httpStatus !== null && (
        <Card title="3 · Response from Cognito's /oauth2/token">
          <KV k="HTTP status" v={httpStatus} />
          <KV k="Raw response body" v={rawResponseText || "(empty)"} pre copyable />
        </Card>
      )}

      {tokenResponse && (
        <>
          <Card title="4 · Parsed token bundle">
            <KV k="token_type" v={tokenResponse.token_type} />
            <KV k="expires_in (s)" v={tokenResponse.expires_in} />
            <KV k="scope" v={tokenResponse.scope} />
            <KV k="id_token" v={tokenResponse.id_token} pre copyable />
            <KV k="access_token" v={tokenResponse.access_token} pre copyable />
            <KV k="refresh_token" v={tokenResponse.refresh_token} pre copyable />
          </Card>

          <Card title="5 · Decoded id_token claims">
            <KV k="claims" v={JSON.stringify(idClaims, null, 2)} pre />
          </Card>

          <Card title="6 · Decoded access_token claims">
            <KV k="claims" v={JSON.stringify(accessClaims, null, 2)} pre />
          </Card>
        </>
      )}
    </div>
  );
};

const pageStyle = {
  padding: "40px 24px",
  maxWidth: 900,
  margin: "0 auto",
  fontFamily:
    "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
  color: "#16181d",
};
const cardStyle = {
  border: "1px solid #d8d4cb",
  padding: 16,
  marginTop: 16,
  background: "#fafaf7",
};

const Card = ({ title, children }) => (
  <div style={cardStyle}>
    <div
      style={{
        fontFamily: "ui-monospace, monospace",
        fontSize: 11,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: "#6b6f78",
        marginBottom: 10,
      }}
    >
      {title}
    </div>
    {children}
  </div>
);

const Section = ({ label, value }) => (
  <div style={{ marginTop: 12, fontFamily: "ui-monospace, monospace", fontSize: 13 }}>
    <span style={{ color: "#6b6f78" }}>{label}: </span>
    <strong>{value}</strong>
  </div>
);

const KV = ({ k, v, pre, copyable }) => (
  <div style={{ marginBottom: 10 }}>
    <div
      style={{
        fontSize: 12,
        color: "#6b6f78",
        marginBottom: 4,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <span>{k}</span>
      {copyable && (
        <button
          onClick={() => copy(String(v))}
          style={{
            fontSize: 11,
            padding: "2px 8px",
            border: "1px solid #d8d4cb",
            background: "white",
            cursor: "pointer",
          }}
        >
          Copy
        </button>
      )}
    </div>
    {pre ? (
      <pre
        style={{
          fontFamily: "ui-monospace, 'JetBrains Mono', Menlo, monospace",
          fontSize: 12,
          background: "#ecebe4",
          padding: 10,
          margin: 0,
          borderRadius: 2,
          maxHeight: 320,
          overflow: "auto",
          whiteSpace: "pre-wrap",
          wordBreak: "break-all",
        }}
      >
        {String(v ?? "")}
      </pre>
    ) : (
      <div
        style={{
          fontFamily: "ui-monospace, 'JetBrains Mono', Menlo, monospace",
          fontSize: 13,
          wordBreak: "break-all",
        }}
      >
        {String(v ?? "")}
      </div>
    )}
  </div>
);

export default OrendaCallback;
